import * as React from "react";
import moment, { type Moment } from "moment";
import { CancellablePromise } from "../../../Utilities/Generic/Promises";
import { getOption, setOption } from "../../../Utilities/Platform/Options/Options";
import LoginUser, { type LoginUserType } from "./LoginUser";
import LoginUserForm from "./LoginUserForm";

type Props = {
    touchNumbers: boolean;
    password: string;
    username: string;
    requirePassword: boolean;
    onChange: (name: string, value: string, callback?: () => void) => void;
    onLogin: () => Promise<void>;
    onClock: () => Promise<void>;
    users: Array<LoginUserType>;
};

type State = {
    selected: null | LoginUserType;
    previous: Array<LoginUserType>;
    users: Array<LoginUserType>;
    loggingIn: null | string;
};

export default class LoginUserList extends React.Component<Props, State> {
    public cancellablePromise: CancellablePromise<any> | undefined;

    public state: State = {
        selected : null,
        previous : [],
        users    : [],
        loggingIn: null,
    };

    public componentDidMount(): void {
        this.updateUsers();
    }

    public componentDidUpdate(prevProps: Readonly<Props>): void {
        if(prevProps.users !== this.props.users) {
            this.updateUsers();
        }
    }

    public componentWillUnmount(): void {
        if(this.cancellablePromise) {
            this.cancellablePromise.cancel();
        }
    }

    public updateUsers = (): void => {
        if(!this.props.users.length) {
            return;
        }

        const previous = [];
        const users = [];

        const startOfDay = moment().startOf("day");

        const oldDeviceUsers = getOption("device_users");
        const updatedUsers: Record<string, Moment> = {};

        for(let i = 0, l = this.props.users.length; i < l; i++) {
            if(oldDeviceUsers[this.props.users[i].id]) {
                const time = moment(oldDeviceUsers[this.props.users[i].id]);

                if(time.isValid() && time.isSameOrAfter(startOfDay)) {
                    updatedUsers[this.props.users[i].id] = time;
                    previous.push(this.props.users[i]);
                } else {
                    users.push(this.props.users[i]);
                }
            } else {
                users.push(this.props.users[i]);
            }
        }

        // Sort users by time of login
        previous.sort((a, b) => {
            return updatedUsers[b.id].valueOf() - updatedUsers[a.id].valueOf();
        });

        const deviceUsers: Record<string, string> = {};

        // Convert moment back into string
        for(const user in updatedUsers) {
            if(Object.prototype.hasOwnProperty.call(updatedUsers, user)) {
                deviceUsers[user] = updatedUsers[user].toISOString(true);
            }
        }

        setOption("device_users", deviceUsers);

        this.setState({
            previous,
            users,
        });
    };

    public loginUser = (user: string): Promise<void> => {
        this.setState({
            loggingIn: user,
        });

        this.cancellablePromise = new CancellablePromise(
            this.props.onLogin()
        );

        return this.cancellablePromise
            .catch(err => {
                if(!err || !err.cancelled) {
                    this.setState({
                        loggingIn: null,
                    });
                }
            });
    };

    public handleSelect = (user: LoginUserType): void => {
        if(this.state.selected) {
            this.handleCancel();
        } else {
            this.props.onChange("password", "");
            this.props.onChange("username", user.username, () => {
                this.setState({
                    selected: user,
                });

                if(!this.props.requirePassword) {
                    // TODO: ONCE onClock IS IMPLEMENTED THIS FUNCTION SHOULD NO LONGER AUTO SUBMIT
                    this.loginUser(user.id);
                }
            });
        }
    };

    public handleCancel = (): void => {
        this.props.onChange("username", "");
        this.props.onChange("password", "");
        this.setState({
            selected: null,
        });
    };

    public render(): React.ReactNode {
        const classes = [];

        if(this.state.selected) {
            classes.push("user-selected");
        }

        if(this.state.loggingIn) {
            classes.push("logging-in");
        }

        return (
            <div id="login-user-list" className={classes.join(" ")}>
                {this.state.previous.map((user) => (
                    <LoginUser
                        key={user.id}
                        onClick={this.handleSelect}
                        user={user}
                        loggingIn={this.state.loggingIn === user.id}
                    />
                ))}
                {(!!this.state.previous.length && !!this.state.users.length) && (
                    <div className="login-user-list-separator" />
                )}
                {this.state.users.map((user) => (
                    <LoginUser
                        key={user.id}
                        onClick={this.handleSelect}
                        user={user}
                        loggingIn={this.state.loggingIn === user.id}
                    />
                ))}
                <LoginUserForm
                    hidden={!(this.state.selected && this.props.requirePassword)}
                    user={this.state.selected}
                    username={this.props.username}
                    password={this.props.password}
                    touchNumbers={this.props.touchNumbers}
                    requirePassword={this.props.requirePassword}
                    onChange={this.props.onChange}
                    onLogin={this.props.onLogin}
                    onClock={this.props.onClock}
                    onCancel={this.handleCancel}
                />
            </div>
        );
    }
}
